import axios from "axios";

// Drop-in replacement for a bare `axios` import: same request-config shape
// and method surface, but wired for cookie-based auth — every call carries
// credentials, mutating calls carry the CSRF header, and an expired access
// token gets silently refreshed once before the caller ever sees a 401.
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_HOST,
  withCredentials: true,
});

const SAFE_METHODS = new Set(["get", "head", "options"]);

const getCsrfTokenFromCookie = () => {
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
};

apiClient.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (!SAFE_METHODS.has(method)) {
    const csrfToken = getCsrfTokenFromCookie();
    if (csrfToken) {
      config.headers["X-CSRF-Token"] = csrfToken;
    }
  }
  return config;
});

// Concurrent requests that all hit a 401 at once (e.g. several widgets
// fetching on mount after the access token expired) share this single
// in-flight /refresh call instead of each rotating the refresh token
// themselves, which would invalidate one another's retry.
let refreshPromise = null;

const NO_REFRESH_RETRY_PATHS = ["/login", "/refresh"];

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    const isRefreshExempt =
      config && NO_REFRESH_RETRY_PATHS.some((path) => config.url?.startsWith(path));

    if (response?.status !== 401 || !config || config._retriedAfterRefresh || isRefreshExempt) {
      return Promise.reject(error);
    }
    config._retriedAfterRefresh = true;

    if (!refreshPromise) {
      refreshPromise = apiClient.post("/refresh").finally(() => {
        refreshPromise = null;
      });
    }

    try {
      await refreshPromise;
      return apiClient(config);
    } catch (refreshError) {
      // No recoverable session (e.g. a never-logged-in visitor hitting a
      // protected endpoint) — just propagate the original error. UserContext
      // already reacts to this by setting userDetails to null, which drives
      // AuthProvider's own SPA navigation back to "/". A forced
      // window.location redirect here would reload the page even when
      // already on "/", which reloads UserContext, which repeats this exact
      // 401-then-failed-refresh sequence — an infinite reload loop.
      return Promise.reject(error);
    }
  }
);

export default apiClient;
